import type { RolesResponsibilitiesContent } from '@/blocks/domain/types/BlockContent/RolesResponsibilitiesContent';
import type { BlockContentDto } from '@/blocks/application/mappers/blockMapper';
import { fromRolesColumns, mapRolesColumns } from '@/blocks/application/mappers/blockColumnsMapper';

type RolesContentDto = Extract<BlockContentDto, { kind: 'roles_responsibilities' }>;
type LegacyRoleRow = RolesResponsibilitiesContent['rows'][number];

function textField(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Older seeds stored party rows under `items` and omitted `rows`. Either shape must load. */
function rowSource(dto: RolesContentDto): unknown {
  if (Array.isArray(dto.rows)) return dto.rows;
  const legacy = dto as RolesContentDto & { items?: unknown };
  return Array.isArray(legacy.items) ? legacy.items : undefined;
}

function mapLegacyRows(source: unknown): LegacyRoleRow[] {
  if (!Array.isArray(source)) return [];

  const rows: LegacyRoleRow[] = [];
  for (const entry of source) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const row: LegacyRoleRow = {
      role: textField(record.role),
      responsibility: textField(record.responsibility ?? record.text)
    };
    if (typeof record.id === 'string' && record.id.length > 0) row.id = record.id;
    if (record.hidden === true) row.hidden = true;
    rows.push(row);
  }
  return rows;
}

function mapCategories(categories: RolesContentDto['categories']): RolesResponsibilitiesContent['categories'] {
  if (!categories || categories.length === 0) return undefined;
  return categories.map((category) => ({
    ...category,
    rows: Array.isArray(category.rows) ? category.rows : []
  }));
}

export function mapRolesResponsibilitiesContent(dto: RolesContentDto): RolesResponsibilitiesContent {
  const mapped: RolesResponsibilitiesContent = {
    kind: 'roles_responsibilities',
    title: typeof dto.title === 'string' ? dto.title : '',
    rows: mapLegacyRows(rowSource(dto)),
    columns: mapRolesColumns(dto)
  };
  const categories = mapCategories(dto.categories);
  if (categories) mapped.categories = categories;
  return mapped;
}

export function fromRolesResponsibilitiesContent(
  content: RolesResponsibilitiesContent
): Extract<BlockContentDto, { kind: 'roles_responsibilities' }> {
  const dto: Extract<BlockContentDto, { kind: 'roles_responsibilities' }> = {
    kind: 'roles_responsibilities',
    title: content.title,
    rows: (content.rows ?? []).map((row) => ({
      ...(row.id ? { id: row.id } : {}),
      role: row.role,
      responsibility: row.responsibility,
      // Stored only when true, like the load side: a hidden row must stay hidden for every reader of `rows`.
      ...(row.hidden === true ? { hidden: true } : {})
    })),
    columns: fromRolesColumns(content.columns)
  };
  if (content.categories && content.categories.length > 0) {
    dto.categories = content.categories;
  }
  return dto;
}
